import { useMemo } from 'react';

import { useTheme } from '@mui/material/styles';

import {
  buildChartTheme,
  Axis, // any of these can be non-animated equivalents
  Grid,
  LineSeries,
  XYChart as VisxXYChart,
  Tooltip,
} from '@visx/xychart';

import { RenderTooltipParams } from '@visx/xychart/lib/components/Tooltip';
import dayjs from 'dayjs';

import { COLOR_WHEEL } from '../../constants';
import { XYChartData, XYChartGridData } from '../../types';

type Datum = {
  x: Date;
  y: number | undefined;
};

const accessors = {
  xAccessor: (d: Datum) => dayjs(d.x).format('MMM D, YYYY'),
  yAccessor: (d: Datum) => d.y,
};

interface Props {
  data: XYChartData;
}

// XY chart requires data to be normalized and behaves unexpectedly otherwise
// in this function we ensure all line series have same x axis values in same order
// if some series have missing values - we set those to null
// which is reflected in the chart as missing value
const normalizeData = (data: XYChartData): XYChartData => {
  const allDates = [
    ...new Set(
      data
        .flatMap((d) => d.data)
        .sort((a, b) => dayjs(a.x).valueOf() - dayjs(b.x).valueOf())
        .map((d) => d.x),
    ),
  ];

  const normalizedData = data.map((originalDataRow) => ({
    ...originalDataRow,
    data: [] as XYChartGridData,
  }));

  for (const date of allDates) {
    // we need index in both normalizedData and data
    // eslint-disable-next-line unicorn/no-for-loop
    for (let dataIndex = 0; dataIndex < data.length; dataIndex++) {
      const originalEntry = data[dataIndex].data.find((d) => d.x === date);

      if (originalEntry) {
        normalizedData[dataIndex].data.push(originalEntry);
      } else {
        normalizedData[dataIndex].data.push({
          x: date,
          y: undefined,
        });
      }
    }
  }

  return normalizedData;
};

export const XYChart = ({ data }: Props) => {
  const { palette } = useTheme();
  const theme = buildChartTheme({
    backgroundColor: palette.background.paper,
    colors: COLOR_WHEEL,
    gridColor: palette.divider,
    gridColorDark: palette.divider,
    svgLabelBig: { fill: palette.text.primary },
    svgLabelSmall: { fill: palette.text.primary },
    tickLength: 10,
  });

  const normalizedData = useMemo(() => normalizeData(data), [data]);

  const servicesComponents = normalizedData.map((d) => {
    return <LineSeries key={d.dataKey} data={d.data} dataKey={d.dataKey} {...accessors} />;
  });

  return (
    <VisxXYChart height={300} theme={theme} xScale={{ type: 'band' }} yScale={{ type: 'linear' }}>
      <Axis orientation="bottom" />
      <Axis orientation="left" />
      <Grid columns={false} numTicks={4} />
      {servicesComponents}
      <Tooltip
        renderTooltip={({ colorScale, tooltipData }: RenderTooltipParams<Datum>) => {
          const nearestDatum = tooltipData?.nearestDatum;
          if (!colorScale || !nearestDatum) {
            return null;
          }

          return (
            <div>
              <div style={{ color: colorScale(nearestDatum.key) }}>{nearestDatum.key}</div>
              {accessors.xAccessor(nearestDatum.datum)}
              {', '}
              {accessors.yAccessor(nearestDatum.datum)}
            </div>
          );
        }}
        showSeriesGlyphs={true}
        showVerticalCrosshair={true}
        snapTooltipToDatumX={true}
        snapTooltipToDatumY={true}
      />
    </VisxXYChart>
  );
};
